import React from 'react';
import { useCart } from '../Context/CartContext';
import { Link } from 'react-router-dom';
import { useLanguage, getLanguageText } from '../Context/LanguageContext';

export default function CartDrawer() {
  const { 
    cartItems, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal, 
    getCartItemPricing,
    isCartOpen, 
    toggleCart 
  } = useCart();
  const { language } = useLanguage();

  if (!isCartOpen) return null;

  return (
    <>
      <div className="cart-overlay" onClick={toggleCart}></div>
      <div className={`cart-drawer ${isCartOpen ? 'open' : ''}`}>
        <div className="cart-header">
          <h2 className="h3">Your Cart</h2>
          <button className="close-cart-btn" onClick={toggleCart}>
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>

        <div className="cart-items">
          {cartItems.length === 0 ? (
            <div className="empty-cart">
              <p>Your cart is empty.</p>
              <button className="btn" onClick={toggleCart}>Continue Shopping</button>
            </div>
          ) : (
            cartItems.map(item => {
              // Determine display name
              let displayName = item.name;
              if (item.nameEn || item.nameAr) {
                 // If we have separate fields, we can use getLanguageText more effectively
                 // But getLanguageText expects "En / Ar" string or object.
                 // item.name is likely "En / Ar" from Firestore.
                 displayName = getLanguageText(item.name, language);
              }

              // Determine image
              const itemImage = (item.imageUrls && item.imageUrls[0]) || 
                                (item.images && item.images[0]) || 
                                item.imageUrl || 
                                'https://placehold.co/100x100';

              const pricing = getCartItemPricing ? getCartItemPricing(item) : {
                baseUnitPrice: (item.selectedPrice || item.price || 0),
                finalUnitPrice: (item.selectedPrice || item.price || 0),
                badge: null
              };

              const hasItemDiscount = pricing.finalUnitPrice < pricing.baseUnitPrice;

              return (
              <div key={item.id} className="cart-item">
                <div className="cart-item-image">
                  <img src={itemImage} alt={displayName} />
                </div>
                <div className="cart-item-details">
                  <h4>{displayName}</h4>
                  {hasItemDiscount ? (
                    <p className="price" style={{ display: 'flex', gap: '10px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ color: '#6b7280', textDecoration: 'line-through', fontWeight: 700, fontSize: '0.95rem' }}>
                        {pricing.baseUnitPrice} EGP
                      </span>
                      <span style={{ color: 'var(--portland-orange)', fontWeight: 800, fontSize: '1.1rem' }}>
                        {pricing.finalUnitPrice} EGP
                      </span>
                      <span style={{ background: '#ff4d4f', color: '#fff', fontWeight: 800, fontSize: '0.75rem', padding: '2px 8px', borderRadius: '6px' }}>
                        {pricing.badge || 'OFF'}
                      </span>
                    </p>
                  ) : (
                    <p className="price">{pricing.finalUnitPrice} EGP</p>
                  )}
                  {item.selectedTaste && (
                    <p className="taste" style={{ fontSize: '0.8rem', color: '#666', margin: '5px 0' }}>
                      {typeof item.selectedTaste === 'object' ? item.selectedTaste.name : item.selectedTaste}
                    </p>
                  )}
                  <div className="quantity-controls">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                  </div>
                </div>
                <button className="remove-item-btn" onClick={() => removeFromCart(item.id)}>
                  <ion-icon name="trash-outline"></ion-icon>
                </button>
              </div>
            )})
          )}
        </div>

        {cartItems.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total">
              <span>Total:</span>
              <span className="total-amount">{getCartTotal()} EGP</span>
            </div>
            <Link to="/checkout" className="btn checkout-btn" onClick={toggleCart}>
              Proceed to Checkout
            </Link>
          </div>
        )}
      </div>

      <style>{`
        .cart-overlay {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background: rgba(0, 0, 0, 0.5) !important;
          z-index: 19999 !important;
          display: block !important;
        }
        .cart-drawer {
          position: fixed !important;
          top: 0 !important;
          right: 0 !important;
          width: 100% !important;
          max-width: 500px !important;
          height: 100vh !important;
          background: #fff !important;
          z-index: 20000 !important;
          padding: 25px !important;
          display: flex !important;
          flex-direction: column !important;
          box-shadow: -5px 0 25px rgba(0,0,0,0.15) !important;
          transform: translateX(100%) !important;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .cart-drawer.open {
          transform: translateX(0) !important;
        }
        
        @media (max-width: 576px) {
          .cart-drawer {
            max-width: 100% !important;
            width: 100% !important;
          }
        }

        .cart-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          margin-bottom: 20px !important;
          border-bottom: 1px solid #eee !important;
          padding-bottom: 15px !important;
          flex-shrink: 0 !important;
        }
        .close-cart-btn {
          font-size: 28px !important;
          background: none !important;
          border: none !important;
          cursor: pointer !important;
          color: #333 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 40px !important;
          height: 40px !important;
          border-radius: 50% !important;
          transition: background 0.2s !important;
        }
        .close-cart-btn:hover {
          background-color: #f5f5f5 !important;
        }
        .cart-items {
          flex: 1 !important;
          overflow-y: auto !important;
          max-height: none !important;
          padding: 10px 5px !important;
        }
        .cart-item {
          display: flex !important;
          gap: 15px !important;
          margin-bottom: 20px !important;
          border-bottom: 1px solid #f5f5f5 !important;
          padding-bottom: 15px !important;
        }
        .cart-item-image img {
          width: 100px !important;
          height: 100px !important;
          object-fit: cover !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
        }
        .cart-item-details {
          flex: 1 !important;
        }
        .cart-item-details h4 {
          font-size: 1rem !important;
          margin-bottom: 5px !important;
          font-weight: 700 !important;
          color: #333 !important;
        }
        .cart-item-details .price {
          color: var(--portland-orange) !important;
          font-weight: bold !important;
          margin-bottom: 10px !important;
          font-size: 1.1rem !important;
        }
        .quantity-controls {
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
        }
        .quantity-controls button {
          width: 28px !important;
          height: 28px !important;
          border: 1px solid #ddd !important;
          background: #fff !important;
          border-radius: 4px !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-size: 1.2rem !important;
          color: #555 !important;
        }
        .remove-item-btn {
          color: #ff4d4d !important;
          font-size: 22px !important;
          align-self: flex-start !important;
          background: none !important;
          border: none !important;
          cursor: pointer !important;
          padding: 5px !important;
        }
        .cart-footer {
          border-top: 1px solid #eee !important;
          padding-top: 20px !important;
          margin-top: auto !important;
          flex-shrink: 0 !important;
          background: #fff !important;
        }
        .cart-total {
          display: flex !important;
          justify-content: space-between !important;
          font-size: 1.3rem !important;
          font-weight: 800 !important;
          margin-bottom: 20px !important;
          color: #222 !important;
        }
        .checkout-btn {
          width: 100% !important;
          text-align: center !important;
          display: block !important;
          padding: 15px !important;
          font-size: 1.1rem !important;
          font-weight: 700 !important;
          background-color: var(--portland-orange) !important;
          color: white !important;
          border-radius: 8px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
        }
        .empty-cart {
          text-align: center !important;
          padding-top: 50px !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
        }
        .empty-cart p {
          margin-bottom: 20px !important;
          color: #666 !important;
          font-size: 1.1rem !important;
        }
      `}</style>
    </>
  );
}
